import { methodLabel } from "./variationRank";

// Histórico de gerações (spec prompt editável §2 E6/E7): substitui o antigo bloco "Detalhes".

const usd = (v) => `US$ ${Number(v || 0).toFixed(2)}`;
const pct = (v) => `${Math.round(Number(v) * 100)}%`;
const REPEATABLE = ["outpaint-up", "remove-expand", "abstract"];

const batchKey = (g) => g.batchId || `#${g.id}`;
const timeOf = (g) => Date.parse(g.submittedAt || g.recipe?.createdAt || g.completedAt || "") || 0;
const legacyPrompt = (g) => (g.prompt && g.prompt !== "-" ? g.prompt : null);

/** Opção do seletor "Métodos" que repete a receita, ou null quando não há como repetir (correção, motivo). */
export const methodOptionFor = (recipe) => {
    if (!recipe || !REPEATABLE.includes(recipe.method)) return null;
    if (recipe.method === "outpaint-up") return "outpaint-up";
    const wanted = recipe.params?.methods || [];
    if (recipe.method === "remove-expand" || wanted.includes("remove-expand")) return "ace";
    return "abstract";
};

/**
 * Janela de cada lote: do envio até o envio do lote seguinte. Uma nota de rejeição nessa janela
 * é o desfecho das gerações do lote; o lote mais novo é o que está "em revisão".
 */
const isCorrection = (g) => g.method === "correction";

// Correção não é lote da IA: não abre janela, senão roubava a nota de rejeição e o "em revisão".
const batchWindows = (generations) => {
    const starts = {};
    generations.filter((g) => !isCorrection(g)).forEach((g) => {
        const k = batchKey(g);
        const t = timeOf(g);
        if (starts[k] === undefined || t < starts[k]) starts[k] = t;
    });
    const ordered = Object.entries(starts).sort((a, b) => a[1] - b[1]);
    const windows = {};
    ordered.forEach(([k, start], i) => {
        windows[k] = { start, end: i + 1 < ordered.length ? ordered[i + 1][1] : Infinity, latest: i === ordered.length - 1 };
    });
    return windows;
};

export const outcomeFor = (label, g, windows) => {
    const approved = label.approved;
    if (approved?.generationId && approved.generationId === g.id) return { tone: "ok", text: `aprovada v${approved.version}` };
    if (isCorrection(g)) return { tone: "muted", text: "correção enviada" };
    if (g.error) return { tone: "bad", text: `falhou: ${g.error}` };
    const w = windows[batchKey(g)];
    const note = w && [...(label.reviewNotes || [])].reverse().find((n) => {
        const t = Date.parse(n.at || "");
        return t >= w.start && t < w.end;
    });
    if (note) return { tone: "bad", text: `rejeitada: ${note.text}` };
    if (w?.latest && ["queued", "generating"].includes(label.status)) return { tone: "info", text: "em andamento" };
    if (w?.latest && label.status === "review") return { tone: "info", text: "em revisão" };
    return { tone: "muted", text: "não aprovada" };
};

/** Configurações do passo Gerar que repetem a geração (o lote inteiro, não só este provider). */
export const repeatSettings = (label, g) => {
    const option = methodOptionFor(g.recipe);
    if (!option) return null;
    const siblings = (label.generations || []).filter((x) => batchKey(x) === batchKey(g) && x.recipe);
    const counted = option === "ace" ? siblings.filter((x) => x.recipe.method === "remove-expand") : siblings.filter((x) => x.recipe.method === g.recipe.method);
    const total = counted.reduce((acc, x) => acc + (Number(x.recipe.numImages ?? x.numImages) || 0), 0);
    // flux2 não usa prompt nem seed: pega o primeiro provider do lote que usou
    const pool = [g, ...siblings.filter((x) => x.id !== g.id)];
    const first = (field) => pool.map((x) => x.recipe[field]).find((v) => v !== null && v !== undefined);
    return {
        methodOption: option,
        numImages: Math.min(4, Math.max(1, total || 2)),
        prompt: first("prompt") ?? null,
        negativePrompt: first("negativePrompt") ?? null,
        seed: first("seed") ?? null,
        source: g.recipe.source || null,
    };
};

const authorOf = (g, currentUserId) => {
    const id = g.recipe?.createdBy || g.recipe?.uploadedBy;
    if (!id) return null;
    if (currentUserId && String(id) === String(currentUserId)) return "por você";
    return `por usuário …${String(id).slice(-6)}`;
};

const sourceText = (source) => {
    if (!source) return null;
    const frame = source.frameType === "full_art" ? "full art" : "padrão";
    const era = source.era && source.era !== "unknown" ? ` · era ${source.era}` : "";
    const c = source.crop;
    const crop = c ? ` · recorte topo ${pct(c.top)}, base ${pct(c.bottom)}, esquerda ${pct(c.left)}, direita ${pct(c.right)}` : "";
    return `${frame}${era}${crop}`;
};

/** Resumo curto de uma receita (cabeçalho da aprovada): método · provider · seed. */
export const recipeSummary = (recipe) => (recipe
    ? [methodLabel(recipe.method), recipe.provider !== "local" ? recipe.provider : null, recipe.seed !== null && recipe.seed !== undefined ? `seed ${recipe.seed}` : null].filter(Boolean).join(" · ")
    : "");

const TONE = { ok: "text-green-400", bad: "text-red-400", info: "text-[#d59106]", muted: "text-gray-500" };

const HistoryItem = ({ label, g, windows, currentUserId, onRepeat, onUsePrompt }) => {
    const r = g.recipe || null;
    const outcome = outcomeFor(label, g, windows);
    const when = g.submittedAt || r?.createdAt;
    const head = [
        when ? new Date(when).toLocaleString("pt-BR") : null,
        authorOf(g, currentUserId),
        methodLabel(r?.method || g.method),
        r?.provider || g.provider,
        r?.model || g.model,
        g.costUsd ? usd(g.costUsd) : null,
    ].filter(Boolean).join(" · ");
    const promptText = r ? r.prompt : legacyPrompt(g);
    const repeat = r ? repeatSettings(label, g) : null;
    return (
        <li data-testid={`history-${g.id}`} className="border-t border-[#333] py-1">
            <div>{head} · <strong className={TONE[outcome.tone]}>{outcome.text}</strong></div>
            {!r ? <p className="text-xs text-amber-300">receita incompleta</p> : null}
            <details className="text-xs">
                <summary className="cursor-pointer text-gray-400">Receita</summary>
                {promptText ? <p className="whitespace-pre-wrap">Prompt: {promptText}</p> : null}
                {r && !promptText ? <p className="text-gray-400">{r.provider === "local" ? "sem IA: sem prompt" : "este provedor não usa prompt"}</p> : null}
                {r?.negativePrompt ? <p className="whitespace-pre-wrap">Negativo: {r.negativePrompt}</p> : null}
                {r ? <p>{r.seed !== null && r.seed !== undefined ? `seed ${r.seed}` : "sem seed (aleatória ou não usada)"}{r.numImages ? ` · ${r.numImages} imagem(ns)` : ""}</p> : null}
                {r?.source ? <p>Fonte: {sourceText(r.source)}</p> : null}
            </details>
            <div className="flex flex-wrap gap-3 text-xs mt-1">
                {repeat ? <button type="button" className="underline text-[#d59106]" onClick={() => onRepeat(repeat)}>Repetir com estas configurações</button> : null}
                {promptText ? <button type="button" className="underline text-[#d59106]" onClick={() => onUsePrompt(promptText)}>Usar este prompt</button> : null}
            </div>
        </li>
    );
};

const GenerationHistory = ({ label, currentUserId, onRepeat, onUsePrompt }) => {
    const all = label.generations || [];
    const windows = batchWindows(all);
    const newestFirst = all.slice().reverse();
    const totalCost = all.reduce((acc, g) => acc + (Number(g.costUsd) || 0), 0);
    return (
        <details data-testid="generation-history" className="border border-[#333] p-2">
            <summary className="cursor-pointer text-[#d59106]">Histórico de gerações</summary>
            <p className="mt-1">Custo total das gerações: {usd(totalCost)}</p>
            {newestFirst.length ? (
                <ul className="ml-2">
                    {newestFirst.map((g) => (
                        <HistoryItem key={g.id} label={label} g={g} windows={windows} currentUserId={currentUserId} onRepeat={onRepeat} onUsePrompt={onUsePrompt} />
                    ))}
                </ul>
            ) : <p className="text-gray-500">Nenhuma geração ainda.</p>}
            {label.reviewNotes?.length ? (
                <>
                    <p className="text-[#d59106] mt-2">Notas de revisão</p>
                    <ul className="list-disc ml-4">{label.reviewNotes.map((n, i) => <li key={i}>{n.at ? new Date(n.at).toLocaleString("pt-BR") : ""} — {n.text}</li>)}</ul>
                </>
            ) : null}
        </details>
    );
};

export default GenerationHistory;
